/**
 * 🔑 CAMBIAR CONTRASEÑA Y EMAIL DE UNA CUENTA
 *
 * La pantalla de Usuarios dejaba escribir una contraseña nueva al editar y
 * decía "Usuario actualizado correctamente", pero solo escribía la ficha
 * `usuarios/{uid}` de Firestore. La contraseña vive en Firebase Auth, así que
 * nunca cambiaba; con el email pasaba lo mismo (cambiaba la ficha, no la cuenta
 * con la que se entra). Y el "Cambiar contraseña" de la Configuración era un
 * `setTimeout` con un `alert` de éxito.
 *
 * Dos caminos, porque Firebase no deja otra:
 *   - la de OTRA cuenta solo la cambia el Admin SDK → el backend
 *     (`POST /api/usuarios/:uid/credenciales`, que además exige que quien llama
 *     sea admin);
 *   - la PROPIA la cambia el SDK web, tras volver a pedir la contraseña actual
 *     (`reauthenticateWithCredential` + `updatePassword`).
 */

import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';

import { auth } from '../config/firebase';
import { getBackendConfig } from '../config/backendConfig';
import { fetchConReintento, SinSesionError } from '../utils/peticionesBackend';

/**
 * Qué credenciales hay que llevar a Firebase Auth al guardar la edición de un
 * usuario. La contraseña, si se escribió algo; el email, si cambió (Firebase lo
 * guarda en minúsculas, así que cambiar solo mayúsculas no cuenta).
 *
 * @param {object} usuario  la ficha tal como estaba (`email`)
 * @param {object} form     lo que hay en el formulario (`password`, `email`)
 * @returns {{password?: string, email?: string} | null} null si no hay nada que cambiar
 */
export function cambiosDeCredenciales(usuario, form) {
  const cambios = {};

  if (form?.password) cambios.password = form.password;

  const emailNuevo = (form?.email || '').trim();
  const emailAntes = (usuario?.email || '').trim();
  if (emailNuevo && emailNuevo.toLowerCase() !== emailAntes.toLowerCase()) {
    cambios.email = emailNuevo;
  }

  return Object.keys(cambios).length ? cambios : null;
}

/**
 * Cambia la contraseña y/o el email de otra cuenta en Firebase Auth, a través
 * del backend. No toca la ficha de Firestore: eso lo hace quien llama, DESPUÉS,
 * para que la ficha no diga un email con el que no se puede entrar.
 *
 * @throws {Error} con `codigo` (NO_ES_ADMIN, EMAIL_EN_USO, USUARIO_NO_ENCONTRADO…)
 *                 y un mensaje ya escrito para el administrador
 */
export async function cambiarCredencialesUsuario(uid, cambios) {
  const { BACKEND_API_URL } = getBackendConfig();

  // Reintentar un arranque en frío es seguro: `updateUser` con los mismos
  // datos deja la cuenta igual aunque la primera petición llegara a aplicarse
  const peticion = await fetchConReintento(
    `${BACKEND_API_URL}/api/usuarios/${encodeURIComponent(uid)}/credenciales`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cambios)
    }
  );

  const datos = await peticion.json().catch(() => ({}));

  if (!peticion.ok || !datos?.success) {
    const error = new Error(datos?.error || `El servidor respondió ${peticion.status}`);
    error.codigo = datos?.codigo || 'ERROR_CREDENCIALES';
    throw error;
  }

  return datos;
}

/** Errores de Firebase Auth al cambiar la propia contraseña, por campo del formulario. */
export function errorDeCambioPropio(error) {
  switch (error?.code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return { currentPassword: 'La contraseña actual no es correcta' };
    case 'auth/weak-password':
      return { newPassword: 'Firebase considera esta contraseña demasiado débil' };
    case 'auth/too-many-requests':
      return { general: 'Demasiados intentos seguidos. Espera unos minutos y vuelve a probar.' };
    case 'auth/network-request-failed':
      return { general: 'No hay conexión con Firebase. Revisa la red e inténtalo de nuevo.' };
    default:
      if (error instanceof SinSesionError) return { general: error.message };
      return { general: 'No se pudo cambiar la contraseña' };
  }
}

/**
 * Cambia la contraseña de la cuenta con la sesión abierta. Firebase exige un
 * inicio de sesión reciente para esto, así que se vuelve a autenticar con la
 * contraseña actual: además es lo que impide que cualquiera que encuentre el
 * panel abierto la cambie.
 *
 * La sesión sigue abierta después: el SDK renueva sus propios tokens.
 *
 * @throws {SinSesionError} si no hay sesión
 * @throws el error de Firebase Auth (tradúcelo con `errorDeCambioPropio`)
 */
export async function cambiarMiContrasena(actual, nueva) {
  const cuenta = auth.currentUser;
  if (!cuenta || !cuenta.email) throw new SinSesionError();

  await reauthenticateWithCredential(cuenta, EmailAuthProvider.credential(cuenta.email, actual));
  await updatePassword(cuenta, nueva);
}
